import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Users, BookOpen, GraduationCap, Calendar, Clock, TrendingUp, AlertCircle, CheckCircle, Eye, Plus, Search, ClipboardCheck, CircleAlert, UserCheck, FileText,
  FormInput,
} from 'lucide-react';
import useFetch from '../constants/useFetch';
import { setTeacherData } from '../redux/slices/teacherDataSlice';
import { API_BASE_URL } from '../constants';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const { user: userInfo } = useSelector((state) => state.auth);
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [searchTerm, setSearchTerm] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const dataUrl = `${API_BASE_URL}/api/web/staff/dashboard-data`;
  const { data: dashboardData, loading: dashboardLoading, error: dashboardError, refetch: refetchDashboard } = useFetch(dataUrl, 'dashboardData');
  // console.log({ userInfo });
  // console.log({subject_name: userInfo?.subject?.subject_name});
  
  // ✅ Dispatch to Redux once fetched
  useEffect(() => {
    if (dashboardData) {
      dispatch(setTeacherData(dashboardData));
    }
  }, [dashboardData, dispatch]);

  // Mock data - in real app, this would come from API
  const teacherStats = {
    totalClasses: dashboardData?.classrooms?.length || 0,
    totalStudents: dashboardData?.students?.length || 0,
    // subject: dashboardData?.subjects?.length > 0 ? dashboardData?.subjects[0]?.subject_name : 'N/A',
    // subject: userInfo?.subject > 0 ? userInfo?.subject?.subject_name : 'N/A',
    subjects: dashboardData?.subjects?.length > 0 ? dashboardData?.subjects : 'N/A',
    incompleteClassResults: dashboardData?.incompleteClassResultCount || 0,
    incompletedStudentResults: dashboardData?.incompleteStudentResultCount || 0,
  };

  // console.log({ subject: teacherStats?.subject });
  // console.log({formteacher: dashboardData?.formTeacherClass});
  
  

  const classes = dashboardData?.classrooms || [];

  const filteredClasses = classes.filter(cls => 
    cls.class_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cls.form_teacher_firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cls.form_teacher_lastname?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100 dark:bg-green-900/20';
      case 'upcoming': return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20';
      case 'completed': return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20';
    }
  };

  const showSubject = dashboardData?.subjects?.length > 0 ? true : false;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-4 sm:p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Welcome back, {userInfo?.firstname || 'Teacher'}!</h1>
            {/* <p className="text-blue-100 mt-1 text-sm sm:text-base">
              {dashboardLoading ? 'Loading your dashboard...' : `Teaching ${userInfo?.subject?.subject_name}`} • {userInfo?.currentSession} Session • {userInfo?.currentTerm} Term
            </p> */}
            <p className="text-blue-100 mt-1 text-sm sm:text-base">
              {dashboardLoading ? (
                'Loading your dashboard...'
              ) : (
                <>
                  Teaching:{" "}
                  {dashboardData?.subjects?.map((sub, i) => (
                    <span key={i}>
                      {sub.subject_name} {" • "}
                    </span>
                  ))}
                  {/* {" • "} */}
                  {userInfo?.currentSession} Session • {userInfo?.currentTerm} Term
                </>
              )}
            </p>

          </div>
          <div className="text-left sm:text-right">
            <p className="text-blue-100 text-xs sm:text-sm">Today's Date</p>
            <p className="text-lg sm:text-xl font-semibold">{new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Total Classes</p>
              {dashboardLoading ? (
                <div className="h-6 sm:h-8 w-12 sm:w-16 bg-gray-300 dark:bg-gray-600 rounded-md animate-pulse"></div>
              ) : (
                <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{teacherStats.totalClasses}</p>
              )}
            </div>
            <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500 flex-shrink-0" />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Total Students</p>
              {dashboardLoading ? (
                <div className="h-6 sm:h-8 w-12 sm:w-16 bg-gray-300 dark:bg-gray-600 rounded-md animate-pulse"></div>
              ) : (
                <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{teacherStats.totalStudents}</p>
              )}
            </div>
            <Users className="w-6 h-6 sm:w-8 sm:h-8 text-green-500 flex-shrink-0" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Subject</p>
              {dashboardLoading ? (
                <div className="h-6 sm:h-8 w-16 sm:w-20 bg-gray-300 dark:bg-gray-600 rounded-md animate-pulse"></div>
              ) : (
                // <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white truncate">{userInfo?.subject?.subject_name}</p>
                <>
                {dashboardData?.subjects?.map((sub, i) => (
                  <p key={i} className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white truncate">{sub.subject_name}</p>
                  ))}
                </>
              )}
            </div>
            <GraduationCap className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500 flex-shrink-0" />
          </div>
        </div>

        {/* <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Incomplete Class Results</p>
              {dashboardLoading ? (
                <div className="h-6 sm:h-8 w-12 sm:w-16 bg-gray-300 dark:bg-gray-600 rounded-md animate-pulse"></div>
              ) : (
                <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{teacherStats.incompleteClassResults}</p>
              )}
            </div>
            <CircleAlert className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500 flex-shrink-0" />
          </div>
        </div> */}
        {dashboardData?.formTeacherClass ? (<div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Form Teacher Class</p>
              {dashboardLoading ? (
                <div className="h-6 sm:h-8 w-12 sm:w-16 bg-gray-300 dark:bg-gray-600 rounded-md animate-pulse"></div>
              ) : (
                <button 
                  className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white"
                  title="View Students"
                  // onClick={() => navigate(`/students?classId=${cls.uuid}`)}
                  // onClick={() => navigate(`/students?classId=${dashboardData?.formTeacherClass?.uuid}`)}
                  onClick={() => navigate(`/form-teacher-students/${dashboardData?.formTeacherClass?.uuid}`)}
                  // onClick={() => navigate(`/form-teacher-students/${dashboardData?.formTeacherClass?.uuid}`)}
                  
                >
                        <span>{dashboardData?.formTeacherClass?.class_name}</span>
                      </button>
                // <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{dashboardData?.formTeacherClass?.class_name}</p>
              )}
            </div>
            <FormInput className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500 flex-shrink-0" />
          </div>
        </div>):null}

      </div>

      {/* Assigned Classes */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Assigned Classes</h2>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search classes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-4 sm:p-6">
          {dashboardLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded-lg animate-pulse"></div>
                    <div className="space-y-2">
                      <div className="h-4 w-32 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                      <div className="h-3 w-24 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <div className="h-8 w-20 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                    <div className="h-8 w-20 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                    <div className="h-8 w-20 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredClasses.length > 0 ? (
            <div className="space-y-4">
              {filteredClasses.map((cls, i) => (
                <div key={i} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors space-y-3 sm:space-y-0">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 dark:text-white truncate">{cls.class_name}</h3>
                      {/* <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                        {cls.is_form_teacher && <span>Form Teacher: Yes • </span>} {cls.student_count || 0} students
                      </p> */}
                      <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                        {cls.is_form_teacher ? (<span className="text-green-600">⭐ Form Teacher</span>):null}
                        <span>• {cls.student_count || 0} students</span>
                      </p>

                    </div>
                  </div>

                  {showSubject ? (<div>{cls?.subject_name}</div>) : null}
                  
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                    {/* <div className="text-left sm:text-right">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{cls.time || 'N/A'}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{cls.room || 'N/A'}</p>
                    </div> */}
                    
                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2">
                      {/* <button 
                        className="flex items-center space-x-1 p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="View Class"
                      >
                        <Eye className="w-4 h-4" />
                        <span className="text-xs font-medium hidden sm:inline">View Class</span>
                      </button> */}
                      
                      <button 
                        className="flex items-center space-x-1 p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                        title="View Students"
                        // onClick={() => navigate(`/students?classId=${cls.uuid}`)}
                        // onClick={() => navigate(`/students?classId=${cls.uuid}&subjectId=${cls.subject_uuid}`)}
                        onClick={() => navigate(`/students/${cls.uuid}?subjectId=${cls.subject_uuid}`)}

                      >
                        <UserCheck className="w-4 h-4" />
                        <span className="text-xs font-medium hidden sm:inline">Students</span>
                      </button>
                      
                      <button 
                        className="flex items-center space-x-1 p-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                        title="Enter Class Result"
                        onClick={() => navigate(`/results?classOption=${cls?.class_name}`)}
                      >
                        <FileText className="w-4 h-4" />
                        <span className="text-xs font-medium hidden sm:inline">Results</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No classes found</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      {/* <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Quick Actions</h2>
        </div>
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <button className="flex flex-col items-center p-3 sm:p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500 mb-2" />
              <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white text-center">Take Attendance</span>
            </button>
            <button className="flex flex-col items-center p-3 sm:p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-green-500 mb-2" />
              <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white text-center">Create Assignment</span>
            </button>
            <button className="flex flex-col items-center p-3 sm:p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500 mb-2" />
              <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white text-center">View Grades</span>
            </button>
            <button className="flex flex-col items-center p-3 sm:p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <AlertCircle className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500 mb-2" />
              <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white text-center">Reports</span>
            </button>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default Dashboard; 